// src/components/audition-booking-wizard/Step2_Payment.js

import React, { useState } from "react";
import { Button, Form, Row, Col, Spinner, Table, Badge, Alert, Modal } from "react-bootstrap";
import { fetchActiveCouponsApi } from "../../services/api";

const Step2_Payment = ({
  audition,
  isAuthenticated,
  user,
  newRegisterUser,
  apiError,
  authError,
  nameInput,
  setNameInput,
  emailInput,
  setEmailInput,
  mobileInput,
  setMobileInput,
  otpInput,
  setOtpInput,
  showInlineOtpSection,
  isSendingAuthOtp,
  isVerifyingAuthOtp,
  handleResendOtp,
  handleVerifyInlineOtp,
  timer,
  resendDisabled,
  loading,
  couponCode,
  setCouponCode,
  couponData,
  couponError,
  handleApplyCoupon,
  applyingCoupon,
  handleNext,
  selectedMode,
  isPaymentEnabled = true
}) => {
  // Coupon modal states
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  // Load available coupons
  const loadAvailableCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const res = await fetchActiveCouponsApi();
      if (res.data?.success && res.data.data && res.data.data.length > 0) {
        setAvailableCoupons(res.data.data);
      } else {
        setAvailableCoupons([
          {
            id: 1,
            coupon_code: "FREE100",
            coupon_type: "per",
            amount: 100,
            can_use_multiple_time: false,
            description: "Special Launch Offer: 100% Free Audition Booking!"
          }
        ]);
      }
    } catch (err) {
      console.warn("Could not load coupons from API, fallback to default:", err);
      setAvailableCoupons([
        {
          id: 1,
          coupon_code: "FREE100",
          coupon_type: "per",
          amount: 100,
          can_use_multiple_time: false,
          description: "Special Launch Offer: 100% Free Audition Booking!"
        }
      ]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleSelectCoupon = (code) => {
    setCouponCode(code);
    if (typeof handleApplyCoupon === 'function') {
      handleApplyCoupon(code);
    }
    setShowCouponModal(false);
  };

  // ✅ Regex Validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) setNameInput(value);
  };

  const handleEmailChange = (e) => {
    setEmailInput(e.target.value);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setMobileInput(value);
    }
  };

  const isNameValid = nameInput.length >= 3 && nameInput.length <= 50;
  const isEmailValid = emailRegex.test(emailInput);
  const isMobileValid = mobileInput.length === 10;

  const baseAmount =
    Number(
      audition?.price ||
      audition?.final_amount ||
      0
    );
  let auditionTypeCost = 0;

  const auditionCosts =
    audition?.allCosts?.find(
      c => c.type === 'auditionType'
    );

  if (auditionCosts) {
    const details =
      typeof auditionCosts.details === 'string'
        ? JSON.parse(auditionCosts.details || '[]')
        : (auditionCosts.details || []);

    const modeMap = {
      ONLINE: 'online',
      WALKIN: 'walkin',
      F2F: 'facetoface'
    };

    const selectedType =
      modeMap[selectedMode] || '';

    const normalize = (v) =>
      String(v || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '');

    const matched =
      details.find(
        d =>
          normalize(d.label) ===
          normalize(selectedType)
      );

    if (matched) {
      auditionTypeCost =
        Number(matched.value || 0);
    }
  }

  // Combined Gross Amount (Base + Audition Type)
  const grossAmount = baseAmount + auditionTypeCost;
  let discountAmount = 0;

  if (couponData) {
    const discountValue =
      Number(couponData.discount || 0);

    // FIXED DISCOUNT
    if (couponData.type === "fix") {
      discountAmount = discountValue;
    }
    // PERCENTAGE DISCOUNT
    else if (couponData.type === "per") {
      discountAmount =
        (grossAmount * discountValue) / 100;
    }
  }

  // SUBTOTAL
  let subtotal = grossAmount - discountAmount;
  if (subtotal < 0) {
    subtotal = 0;
  }

  // GST AFTER DISCOUNT (9%)
  const gstAmount = subtotal > 0 ? subtotal * 0.09 : 0;
  const finalTotal = Math.round(subtotal + gstAmount);
  const isFree = finalTotal === 0;

  const auditionTypeLabelMap = {
    ONLINE: "Online Audition",
    WALKIN: "Walk-In Audition",
    F2F: "Face To Face Audition"
  };

  const auditionTypeLabel =
    auditionTypeLabelMap[selectedMode] || "Audition";

  const handlePaymentClick = () => {
    if (!isPaymentEnabled && finalTotal > 0) {
      setShowCouponModal(true);
      loadAvailableCoupons();
      return;
    }
    handleNext();
  };

  return (
    <div className="payment-step-container p-4 animate-fade-in">

      {apiError && <Alert variant="danger">{apiError}</Alert>}
      {authError && <Alert variant="danger">{authError}</Alert>}

      <Row className="g-4">

        {/* ================= USER DETAILS ================= */}

        <Col md={4} className="border-end border-light border-opacity-10 pe-md-3 mb-4 mb-md-0">

          <h6 className="section-step-title mb-3">1. YOUR DETAILS:</h6>

          {isAuthenticated ? (
            <div className="user-info-box p-3 rounded-3 mb-4">
              <h6 className="text-white fw-bold mb-1" style={{ fontSize: '13px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{user?.full_name}</h6>
              <p className="small text-white-50 mb-0" style={{ fontSize: '12px' }}>{user?.email}</p>
              <span className="synced-status-badge">
                Synced
              </span>
            </div>
          ) : (
            <>

              {/* NAME */}

              <Form.Group className="mb-3">
                <Form.Control
                  className="custom-input"
                  placeholder="Enter Name"
                  value={nameInput}
                  minLength={3}
                  maxLength={50}
                  onChange={handleNameChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={nameInput && !isNameValid}
                />
                <Form.Control.Feedback type="invalid">
                  Name must be between 3 and 50 characters
                </Form.Control.Feedback>
              </Form.Group>

              {/* EMAIL */}

              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  className="custom-input"
                  placeholder="Enter Email"
                  value={emailInput}
                  maxLength={100}
                  onChange={handleEmailChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={emailInput && !isEmailValid}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid email address
                </Form.Control.Feedback>
              </Form.Group>

              {/* MOBILE */}

              <Form.Group className="mb-4">
                <Form.Control
                  type="tel"
                  className="custom-input"
                  placeholder="Enter Mobile"
                  value={mobileInput}
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onChange={handleMobileChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={mobileInput && !isMobileValid}
                />
                <Form.Control.Feedback type="invalid">
                  Mobile number must be exactly 10 digits
                </Form.Control.Feedback>
              </Form.Group>

              {/* OTP SECTION */}

              {showInlineOtpSection && (
                <div className="mt-4 p-3 bg-dark rounded">

                  <p className="small text-light mb-2">
                    OTP sent to {emailInput || mobileInput}
                  </p>

                  <Form.Group className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      className="custom-input"
                      maxLength={6}
                      value={otpInput}
                      onChange={(e) =>
                        setOtpInput(e.target.value.replace(/\D/g, ""))
                      }
                      disabled={isVerifyingAuthOtp}
                    />
                  </Form.Group>

                  <Button
                    variant="link"
                    onClick={handleResendOtp}
                    className="btn-white small p-0 mb-2"
                    disabled={resendDisabled || isSendingAuthOtp}
                  >
                    {isSendingAuthOtp ? (
                      <Spinner animation="border" size="sm" />
                    ) : resendDisabled ? (
                      `Resend OTP in ${timer}s`
                    ) : (
                      "Resend OTP"
                    )}
                  </Button>

                  <Button
                    className="btn-danger w-100"
                    onClick={handleVerifyInlineOtp}
                    disabled={isVerifyingAuthOtp || otpInput.length !== 6}
                  >
                    {isVerifyingAuthOtp ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Verify OTP & Continue"
                    )}
                  </Button>

                </div>
              )}

            </>
          )}

          {/* COUPON */}

          <p className="small fw-bold mb-2">Coupon Code:</p>

          <div>

            <div className="d-flex gap-2">

              <Form.Control
                className="custom-input"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />

              <Button
                className="btn-coupon-apply"
                onClick={() => handleApplyCoupon()}
                disabled={applyingCoupon}
              >
                {applyingCoupon ? "Applying..." : "Apply"}
              </Button>

            </div>

            {couponError && (
              <p className="text-danger small mt-2 mb-0">
                {couponError}
              </p>
            )}

            {couponData && (
              <p className="text-success small mt-2 mb-0 fw-bold">
                ✓ Coupon Applied Successfully ({couponData.type === 'per' ? `${couponData.discount}% OFF` : `₹${couponData.discount} OFF`})
              </p>
            )}

            <div className="mt-2">
              <Button
                variant="outline-warning"
                size="sm"
                className="w-100 fw-bold py-1"
                style={{ fontSize: '12px', borderStyle: 'dashed' }}
                onClick={() => {
                  setShowCouponModal(true);
                  loadAvailableCoupons();
                }}
              >
                🎟️ Click to View & Apply Available Coupons
              </Button>
            </div>

          </div>

        </Col>

        {/* ================= SUMMARY ================= */}

        <Col md={4} className="border-end border-light border-opacity-10 px-md-3 mb-4 mb-md-0">

          <h6 className="section-step-title mb-3">SUMMARY</h6>

          <div className="booking-summary-box rounded-3">

            {isFree && (
              <div
                className="p-2 mb-3 rounded text-center fw-bold"
                style={{
                  backgroundColor: 'rgba(40, 167, 69, 0.2)',
                  border: '1px solid #28a745',
                  color: '#28a745',
                  fontSize: '13px'
                }}
              >
                🎉 100% FREE AUDITION (₹0)
              </div>
            )}

            <Table borderless size="sm" className="summary-table mb-0">
              <tbody>

                <tr>
                  <td>Amount</td>
                  <td className="text-end fw-bold">
                    ₹{baseAmount}/-
                  </td>
                </tr>
                <tr>
                  <td>{auditionTypeLabel}</td>
                  <td className="text-end fw-bold">
                    + ₹{auditionTypeCost}/-
                  </td>
                </tr>

                {couponData && (
                  <>
                    <tr>
                      <td>
                        {couponData.type === "per"
                          ? `Discount (${couponData.discount}%)`
                          : "Discount"}
                      </td>
                      <td className="text-end text-success fw-bold text-nowrap">
                        - ₹{Math.round(discountAmount)}/-
                      </td>
                    </tr>

                    <tr>
                      <td>Subtotal</td>
                      <td className="text-end fw-bold">
                        ₹{Math.round(subtotal)}/-
                      </td>
                    </tr>
                  </>
                )}

                <tr>
                  <td>
                    <strong>GST (9%)</strong>
                  </td>
                  <td className="text-end fw-bold">
                    + ₹{Math.round(gstAmount)}/-
                  </td>
                </tr>

                <tr className="summary-total-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="text-end fw-bold text-success">
                    ₹{finalTotal}/-
                  </td>
                </tr>

              </tbody>
            </Table>

          </div>

        </Col>

        {/* ================= PAYMENT ================= */}

        <Col md={4} className="ps-md-3">

          <h6 className="section-step-title mb-3">2. PAYMENT :</h6>

          {loading || isSendingAuthOtp || isVerifyingAuthOtp ? (

            <Button className="pay-btn disabled">
              <Spinner animation="border" size="sm" />
              {" "}SYNCING...
            </Button>

          ) : isFree ? (

            <Button
              className="pay-btn mb-3 fw-bold"
              style={{
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.4)',
                fontSize: '14.5px'
              }}
              onClick={handlePaymentClick}
              disabled={
                !isAuthenticated &&
                (!isNameValid || !isEmailValid || !isMobileValid)
              }
            >
              {isAuthenticated
                ? "✅ CONFIRM & APPLY FOR FREE (₹0)"
                : showInlineOtpSection
                ? "Waiting for OTP Verification..."
                : "CONTINUE TO APPLY FOR FREE (₹0)"}
            </Button>

          ) : (

            <Button
              className="pay-btn mb-3"
              onClick={handlePaymentClick}
              disabled={
                !isAuthenticated &&
                (!isNameValid || !isEmailValid || !isMobileValid)
              }
            >
              {isAuthenticated
                ? `PAY ONLINE - ₹${finalTotal}/-`
                : showInlineOtpSection
                ? "Waiting for OTP Verification..."
                : "CONTINUE TO PAYMENT"}
            </Button>

          )}

        </Col>

      </Row>

      {/* ================= COUPON POPUP MODAL ================= */}
      <Modal
        show={showCouponModal}
        onHide={() => setShowCouponModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#1a1a24', color: '#fff', borderBottom: '1px solid #2d2d3a' }}>
          <Modal.Title style={{ fontSize: '17px', fontWeight: 'bold' }}>
            🎟️ Available Coupons & Offers
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#121218', color: '#fff', padding: '20px' }}>
          {loadingCoupons ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="danger" size="sm" />
              <p className="small text-white-50 mt-2 mb-0">Loading available coupons...</p>
            </div>
          ) : availableCoupons.length === 0 ? (
            <div className="text-center py-3 text-white-50">
              No active coupons found at the moment.
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {availableCoupons.map((c, idx) => {
                const isCurrent = couponCode === c.coupon_code && couponData;
                return (
                  <div
                    key={c.id || idx}
                    className="p-3 rounded-3 position-relative"
                    style={{
                      backgroundColor: '#1b1b26',
                      border: isCurrent ? '2px solid #28a745' : '1px dashed rgba(217, 4, 41, 0.7)',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        style={{
                          background: 'rgba(217, 4, 41, 0.2)',
                          color: '#ff4d6d',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          letterSpacing: '1px',
                          fontSize: '14px',
                          fontFamily: 'monospace'
                        }}
                      >
                        {c.coupon_code}
                      </span>
                      <Badge bg="success" style={{ fontSize: '12px' }}>
                        {c.coupon_type === 'per' ? `${c.amount}% OFF` : `₹${c.amount} OFF`}
                      </Badge>
                    </div>

                    <p className="small text-white-50 mb-2" style={{ fontSize: '12.5px', lineHeight: '1.4' }}>
                      {c.coupon_type === 'per' && Number(c.amount) === 100
                        ? "🎉 100% Free Special Launch Offer! Apply this coupon to book this audition without paying any money."
                        : c.description || `Get ${c.amount}${c.coupon_type === 'per' ? '%' : '₹'} discount on this audition.`}
                    </p>

                    <div className="d-flex justify-content-between align-items-center pt-2 border-top border-secondary border-opacity-25">
                      <small className="text-white-50" style={{ fontSize: '11px' }}>
                        {c.can_use_multiple_time ? "Reusable coupon" : "One-time use per candidate"}
                      </small>
                      {isCurrent ? (
                        <Badge bg="success" className="py-2 px-3">
                          ✓ APPLIED
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="danger"
                          className="fw-bold px-3"
                          style={{ fontSize: '12px' }}
                          onClick={() => handleSelectCoupon(c.coupon_code)}
                          disabled={applyingCoupon}
                        >
                          {applyingCoupon ? "Applying..." : "APPLY COUPON"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1a1a24', borderTop: '1px solid #2d2d3a' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowCouponModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Step2_Payment;